// src/pages/AssetsPage.tsx
import { useQueryClient } from '@tanstack/react-query';
import { Filter, Plus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';

import { AssetsEmptyState } from '@/components/assets/EmptyState';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';

import { AssetCard } from '@/components/assets/AssetCard';
import AssetForm from '@/components/assets/AssetForm';
import { UpgradeBanner } from '@/components/subscription/UpgradeBanner';
import { useBackendSubscription } from '@/hooks/useBackendSubscription';
import AssetService from '@/services/AssetService';
import type { Asset } from '@/types/asset';

const AssetsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { limits, isFree } = useBackendSubscription();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentFilter, setCurrentFilter] = useState<string>('all');
  const [isAddAssetDrawerOpen, setIsAddAssetDrawerOpen] = useState(false);

  const hasReachedLimit = assets.length >= limits.maxAssets;

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const assetData = await AssetService.getAssets();
      setAssets(assetData);
    } catch (error) {
      console.error('Error fetching assets:', error);
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAssets();

    // Check if subscription was just updated (from sessionStorage flag)
    const subscriptionUpdated = sessionStorage.getItem('subscription_updated');
    if (subscriptionUpdated) {
      // Clear the flag
      sessionStorage.removeItem('subscription_updated');
      // Invalidate and immediately refetch subscription cache
      // This ensures fresh data when user navigates here after enabling subscription
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      queryClient.refetchQueries({
        queryKey: ['subscription'],
        type: 'active', // Refetch all active queries with this key
      });
    }
  }, [fetchAssets, queryClient]);

  const handleFilterChange = (value: string) => {
    setCurrentFilter(value);
  };

  const handleAddAssetSuccess = () => {
    fetchAssets();
    setIsAddAssetDrawerOpen(false);
  };

  const handleOpenAssetForm = () => {
    if (hasReachedLimit) {
      toast.error('Asset limit reached. Please upgrade to add more assets.');
      return;
    }
    setIsAddAssetDrawerOpen(true);
  };

  const filteredAssets = assets.filter((asset) => {
    if (currentFilter === 'all') return true;
    if (currentFilter === 'active') return asset.investment_status === 'active';
    if (currentFilter === 'sold') return asset.investment_status === 'sold';
    return true;
  });

  return (
    <div className="container mx-auto">
      <div className="flex justify-end items-center mb-6">
        <div className="flex items-center space-x-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                Filter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleFilterChange('all')}>
                All Assets
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleFilterChange('active')}>
                Active Investments
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleFilterChange('sold')}>
                Sold Properties
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button 
            onClick={handleOpenAssetForm} 
            disabled={hasReachedLimit}
            className={hasReachedLimit ? "opacity-50 cursor-not-allowed" : ""}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Asset
          </Button>
        </div>
      </div>

      {hasReachedLimit && isFree && <UpgradeBanner />}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, index) => (
            <Card key={index} className="overflow-hidden">
              <CardContent className="p-0">
                <div className="p-6">
                  <Skeleton className="h-6 w-3/4 mb-4" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredAssets.length === 0 ? (
        <div className="flex justify-center">
          <AssetsEmptyState onAddAsset={handleOpenAssetForm} />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAssets.map((asset) => (
            <AssetCard key={asset.id} asset={asset} />
          ))}
        </div>
      )}

      <AssetForm
        open={isAddAssetDrawerOpen}
        onOpenChange={setIsAddAssetDrawerOpen}
        onAssetAdded={handleAddAssetSuccess}
      />
    </div>
  );
};

export default AssetsPage;
